import { Fieldset } from 'components/Form/FormHelper/Fieldset';
import { SelectCard } from 'components/Form/SelectCards/SelectCard';
import {
  SelectCardsProps,
  Spacing,
  Option,
} from 'components/Form/SelectCards/types';
import { AddCustomOptionButton } from 'components/Form/SelectCards/AddCustomOptionButton';
import React, { useId } from 'react';
import tw from 'twin.macro';

const getSpacingStyles = (spacing: Spacing) =>
  ({
    small: tw`gap-2`,
    medium: tw`gap-3`,
    big: tw`gap-4`,
  }[spacing]);

export function SelectCards<T, IsMulti extends boolean = false>(
  props: SelectCardsProps<T, IsMulti>,
) {
  const {
    options,
    value,
    onChange,
    label,
    direction = 'horizontal',
    size = 'small',
    spacing = 'big',
    isMulti = false,
    variant = 'filled',
    enableCustomOption,
  } = props;

  const reactId = useId();
  const groupId = `selectCards-${reactId}`;
  const spacingStyles = getSpacingStyles(spacing);

  const additionalOptions: Option<T>[] =
    !isMulti && !!value && !options.map(opt => opt.value).includes(value as T)
      ? [{ label: value as string, value: value as T }]
      : [];

  return (
    <Fieldset
      label={label}
      legendStyle={tw`mb-2 text-sm font-medium text-neutral-quiet`}
    >
      <div
        css={[
          spacingStyles,
          tw`flex`,
          direction === 'horizontal' && tw`flex-wrap`,
          direction === 'vertical' && tw`flex-col`,
        ]}
      >
        {options.concat(additionalOptions).map((opt, index) => {
          const isChecked = isMulti
            ? (value as T[]).includes(opt.value)
            : value === opt.value;
          const id = `${groupId}-${index}`;

          return (
            <SelectCard
              key={id}
              id={id}
              option={opt}
              isMulti={isMulti}
              checked={isChecked}
              size={size}
              variant={variant}
              onChange={checked => {
                if (isMulti) {
                  const arrayValue = value as T[];
                  const newValue = checked
                    ? [...arrayValue, opt.value]
                    : arrayValue.filter(v => v !== opt.value);
                  (onChange as (value: T[]) => void)(newValue);
                } else {
                  (onChange as (value: T) => void)(opt.value);
                }
              }}
            />
          );
        })}

        {enableCustomOption && !isMulti && (
          <div tw="self-center h-full">
            <AddCustomOptionButton
              onAdd={v => {
                (onChange as (value: T) => void)(v as T);
              }}
            />
          </div>
        )}
      </div>
    </Fieldset>
  );
}
